/**
 * Подэкраны шагов wizard'а онбординга (группа 3, шаги 1–8).
 *
 * Каждый шаг — отдельный компонент, отображается подряд по UX-потоку. Шаги
 * 1–3 содержат реальные поля + валидацию (canAdvanceFrom), шаги 4–8 —
 * наполненные каркасы с описанием секций (данных/endpoint'ов пока нет).
 */
import { Input } from '@hsc/ui';
import { CAPABILITY_OPTIONS, type WizardData } from './wizardConfig.js';

export interface StepProps {
  data: WizardData;
  patch: (p: Partial<WizardData>) => void;
}

/** Шаг 1 — приглашение управляющего. */
export function StepManager({ data, patch }: StepProps): JSX.Element {
  return (
    <div className="hsc-stack" data-testid="wizard-step-manager">
      <Input label="Название отеля" value={data.hotelName} onChange={(e) => patch({ hotelName: e.target.value })} />
      <Input label="Имя управляющего" value={data.managerFirstName} onChange={(e) => patch({ managerFirstName: e.target.value })} />
      <Input label="Фамилия управляющего" value={data.managerLastName} onChange={(e) => patch({ managerLastName: e.target.value })} />
      <Input label="Email управляющего" type="email" value={data.managerEmail} onChange={(e) => patch({ managerEmail: e.target.value })} />
      <Input label="Приветственное сообщение (необязательно)" value={data.welcomeMessage} onChange={(e) => patch({ welcomeMessage: e.target.value })} />
    </div>
  );
}

/** Шаг 2 — данные отеля. */
export function StepHotel({ data, patch }: StepProps): JSX.Element {
  return (
    <div className="hsc-stack" data-testid="wizard-step-hotel">
      <Input label="URL-слаг (поддомен)" value={data.slug} onChange={(e) => patch({ slug: e.target.value.toLowerCase() })} placeholder="mara" />
      <Input label="Регион" value={data.region} onChange={(e) => patch({ region: e.target.value })} />
      <Input label="Юридическое лицо" value={data.legalEntity} onChange={(e) => patch({ legalEntity: e.target.value })} />
      <Input label="ИНН" value={data.inn} onChange={(e) => patch({ inn: e.target.value.replace(/\D/g, '') })} maxLength={12} />
    </div>
  );
}

/** Шаг 3 — тариф + capabilities. */
export function StepTier({ data, patch }: StepProps): JSX.Element {
  function toggle(key: string): void {
    const set = new Set(data.capabilities);
    if (set.has(key)) set.delete(key);
    else set.add(key);
    patch({ capabilities: Array.from(set) });
  }
  return (
    <div className="hsc-stack" data-testid="wizard-step-tier">
      <label className="admin-field">
        <span>Тариф</span>
        <select value={data.tier} onChange={(e) => patch({ tier: e.target.value })} className="admin-select">
          <option value="starter">Starter</option>
          <option value="pro">Pro</option>
          <option value="enterprise">Enterprise</option>
        </select>
      </label>
      <fieldset className="admin-caps">
        <legend>Возможности (capabilities)</legend>
        {CAPABILITY_OPTIONS.map((c) => (
          <label key={c.key} className="admin-caps__item">
            <input type="checkbox" checked={data.capabilities.includes(c.key)} onChange={() => toggle(c.key)} />
            <span>{c.label}</span>
          </label>
        ))}
      </fieldset>
    </div>
  );
}

/** Шаг 4 — брендинг (каркас). */
export function StepBranding(): JSX.Element {
  return (
    <div className="hsc-stack" data-testid="wizard-step-branding">
      <p className="admin-stub__badge">Логотип, основной цвет, кастомный домен — каркас (TODO backend загрузки логотипа)</p>
    </div>
  );
}

/** Шаг 5 — интеграции (каркас). */
export function StepIntegrations(): JSX.Element {
  return (
    <div className="hsc-stack" data-testid="wizard-step-integrations">
      <p className="admin-stub__badge">OAuth для Bnovo / TravelLine / T-Bank — каркас (TODO backend OAuth-потоков)</p>
    </div>
  );
}

/** Шаг 6 — стартовые данные (каркас). */
export function StepSeed(): JSX.Element {
  return (
    <div className="hsc-stack" data-testid="wizard-step-seed">
      <p className="admin-stub__badge">Шаблон LovArt / CSV-импорт / пустой старт — каркас (TODO backend импорта)</p>
    </div>
  );
}

/** Шаг 7 — проверка биллинга (каркас). */
export function StepBilling(): JSX.Element {
  return (
    <div className="hsc-stack" data-testid="wizard-step-billing">
      <p className="admin-stub__badge">Расчёт первого счёта (тариф × номера + НДС 20%), способ оплаты, триал — каркас (TODO backend расчёта)</p>
    </div>
  );
}

/** Шаг 8 — запуск (резюме перед provision). */
export function StepGoLive({ data }: { data: WizardData }): JSX.Element {
  return (
    <div className="hsc-stack" data-testid="wizard-step-golive">
      <h3>Сводка</h3>
      <ul className="admin-summary">
        <li>Отель: {data.hotelName || '—'}</li>
        <li>Слаг: {data.slug || '—'}</li>
        <li>Управляющий: {data.managerFirstName} {data.managerLastName} ({data.managerEmail || '—'})</li>
        <li>Тариф: {data.tier}</li>
        <li>Возможности: {data.capabilities.length}</li>
      </ul>
      <p className="admin-stub__badge">
        По кнопке «Запустить» вызывается REAL POST /v1/admin/provision-tenant-with-manager
        (атомарное создание отеля + приглашение управляющего).
      </p>
    </div>
  );
}
